import { neoWorkflowController } from "../../AppController";
import { WEEKLY_BRIEFING_NAME } from "../components/WorkflowIdentity";
import type {
  ClinicWorkflowRun,
  MondayBriefingEmail,
  WorkflowRunProgress,
} from "../../hexagon/core/ports/driven/neoWorkflowRepository";
import { openProjectDocument } from "../../pages/Neo/openProjectDocument";
import { APP_CONTEXT_CLOSE, APP_CONTEXT_OPEN } from "./appContext";

/**
 * ui2026: "chat about this workflow". The side panel opens on a new chat
 * headed by the workflow's latest output; the first message the user sends
 * carries that context to Neo (frontend-only — there is no from-workflow-run
 * endpoint yet).
 */

export type WorkflowChatKind = "neo" | "clinic" | "monday";

export const WORKFLOW_CHAT_STARTERS = [
  "Summarize this output",
  "What needs my attention from this?",
] as const;

export interface WorkflowChatOutput {
  fileId: number;
  name: string;
}

export interface WorkflowChatRun {
  runId: string;
  status: string;
  finishedAt: string | null;
  label?: string | null;
  outputs: WorkflowChatOutput[];
}

export interface WorkflowChatEmail {
  subject: string;
  bodyText: string;
}

export interface WorkflowChatContext {
  kind: WorkflowChatKind;
  name: string;
  /** Neo workflow id (kind "neo"). */
  workflowId?: number;
  /** Catalog workflow id string (kind "clinic"). */
  workflowIdStr?: string;
  /** Owning project of a Neo workflow; needed to open its output files. */
  projectId?: number | null;
  /** Latest run, when the caller already has it. Otherwise the panel looks
   *  it up (see resolveLatestWorkflowRun). */
  run?: WorkflowChatRun | null;
  /** Monday briefing: the email that was sent. */
  email?: WorkflowChatEmail | null;
}

export const fromNeoRun = (run: WorkflowRunProgress): WorkflowChatRun => ({
  runId: run.run_id,
  status: run.status,
  finishedAt: run.completed_at ?? run.started_at ?? null,
  label: run.label,
  outputs: run.outputs.map((output) => ({
    fileId: Number(output.file_id),
    name: output.name,
  })),
});

export const fromClinicRun = (run: ClinicWorkflowRun): WorkflowChatRun => ({
  runId: run.run_id,
  status: run.status,
  finishedAt:
    run.sent_at ??
    run.completed_at ??
    run.generated_at ??
    run.created_at ??
    run.started_at ??
    null,
  label: run.label,
  outputs: run.artifacts
    .filter((artifact) => artifact.available !== false)
    .map((artifact) => ({
      fileId: Number(artifact.file_id),
      name: artifact.name || artifact.filename || "Output",
    })),
});

export const fromMondayEmail = (
  email: MondayBriefingEmail
): WorkflowChatEmail => ({
  subject: email.subject || WEEKLY_BRIEFING_NAME,
  bodyText:
    email.body_text?.trim() ||
    htmlToText(email.body_html ?? "") ||
    "No email body stored.",
});

const htmlToText = (html: string): string => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return (doc.body.textContent ?? "").replace(/\n{3,}/g, "\n\n").trim();
};

const recipientEmails = (
  recipients: MondayBriefingEmail["recipients"]
): string[] =>
  Array.isArray(recipients)
    ? recipients
        .map((recipient) =>
          typeof recipient === "string" ? recipient : recipient?.email
        )
        .filter((recipient): recipient is string => Boolean(recipient))
        .map((recipient) => recipient.toLowerCase())
    : [];

const runTime = (run: WorkflowChatRun): number =>
  run.finishedAt ? new Date(run.finishedAt).getTime() || 0 : 0;

/**
 * Fill in the latest run (and, for the Monday briefing, the latest email sent
 * to this user) when the caller only knew which workflow it was.
 */
export const resolveLatestWorkflowRun = async (
  context: WorkflowChatContext,
  { clinicId, userEmail }: { clinicId?: number; userEmail?: string }
): Promise<Pick<WorkflowChatContext, "run" | "email">> => {
  if (context.run !== undefined || context.email !== undefined) {
    return { run: context.run, email: context.email };
  }
  if (context.kind === "neo" && context.workflowId != null) {
    const result = await neoWorkflowController.listWorkflowRuns(
      context.workflowId,
      5
    );
    const runs = (result.data ?? []).map(fromNeoRun);
    const done = runs.filter((run) => run.outputs.length > 0);
    const latest = [...(done.length ? done : runs)].sort(
      (a, b) => runTime(b) - runTime(a)
    )[0];
    return { run: latest ?? null };
  }
  if (context.kind === "clinic" && context.workflowIdStr && clinicId) {
    const result = await neoWorkflowController.listClinicWorkflowRuns(
      clinicId,
      context.workflowIdStr
    );
    const latest = (result.data ?? [])
      .map(fromClinicRun)
      .sort((a, b) => runTime(b) - runTime(a))[0];
    return { run: latest ?? null };
  }
  if (context.kind === "monday" && clinicId) {
    const result = await neoWorkflowController.listMondayBriefingDeliveries(
      clinicId
    );
    const me = userEmail?.toLowerCase();
    // Newest first; the first one actually sent to this user wins.
    for (const delivery of result.data ?? []) {
      const emailResult =
        await neoWorkflowController.getMondayBriefingDeliveryEmail(delivery.id);
      if (!emailResult.success || !emailResult.data) continue;
      if (me && !recipientEmails(emailResult.data.recipients).includes(me)) {
        continue;
      }
      return {
        run: {
          runId: String(delivery.run_id ?? delivery.id),
          status: delivery.status,
          finishedAt: delivery.finished_at ?? delivery.started_at ?? null,
          outputs: [],
        },
        email: fromMondayEmail(emailResult.data),
      };
    }
    return { run: null, email: null };
  }
  return { run: null };
};

/** Open an output file the same way the Workflows page does. */
export const openWorkflowOutput = async (
  context: WorkflowChatContext,
  run: WorkflowChatRun,
  output: WorkflowChatOutput,
  clinicId?: number
): Promise<boolean> => {
  if (context.kind === "neo") {
    await openProjectDocument({
      projectId: context.projectId,
      fileId: output.fileId,
      name: output.name,
    });
    return true;
  }
  if (context.kind === "clinic" && clinicId) {
    const result = await neoWorkflowController.getClinicWorkflowArtifactUrl(
      clinicId,
      run.runId,
      output.fileId
    );
    const url = result.data?.url ?? result.data?.download_url;
    if (!result.success || !url) return false;
    window.open(url, "_blank", "noopener,noreferrer");
    return true;
  }
  return false;
};

/** Email bodies can be long; keep the first message a sensible size. */
const MAX_EMAIL_CHARS = 12_000;

/**
 * Hidden context appended (as its own text part) to the first message of a
 * workflow chat, so Neo knows exactly which workflow, run and output the
 * user is asking about.
 */
export const workflowContextText = (
  context: WorkflowChatContext,
  timeZone?: string | null
): string => {
  const run = context.run;
  const when = run?.finishedAt
    ? new Date(run.finishedAt).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
        ...(timeZone ? { timeZone } : {}),
      })
    : null;
  const lines = [
    "The user opened this chat from a workflow's latest output in the app.",
    "Answer their question about this workflow and output.",
    `Workflow: ${context.name}`,
    context.workflowId != null ? `Workflow id: ${context.workflowId}` : null,
    context.workflowIdStr ? `Workflow key: ${context.workflowIdStr}` : null,
    run ? `Run id: ${run.runId} (${run.status})` : "No run has finished yet.",
    when ? `Run finished: ${when}` : null,
    run?.outputs.length
      ? `Output files: ${run.outputs
          .map((output) => `${output.name} (file id ${output.fileId})`)
          .join("; ")}`
      : null,
  ].filter(Boolean);
  const email = context.email
    ? `\nEmail subject: ${
        context.email.subject
      }\nEmail body:\n${context.email.bodyText.slice(0, MAX_EMAIL_CHARS)}`
    : "";
  return `\n\n${APP_CONTEXT_OPEN}\n${lines.join(
    "\n"
  )}${email}\n${APP_CONTEXT_CLOSE}`;
};

/** Machine-readable copy for ``neo_ui_context.extra`` (seen via get_scope). */
export const workflowContextExtra = (
  context: WorkflowChatContext
): Record<string, unknown> => ({
  surface: "workflow_output",
  workflow_name: context.name,
  workflow_kind: context.kind,
  ...(context.workflowId != null ? { workflow_id: context.workflowId } : {}),
  ...(context.workflowIdStr ? { workflow_id_str: context.workflowIdStr } : {}),
  ...(context.run
    ? {
        run_id: context.run.runId,
        output_file_ids: context.run.outputs.map((output) => output.fileId),
      }
    : {}),
});
